import { useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  NativeSelect,
  Portal,
  Text,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';
import useGetBehaviours from '../useGetBehaviours';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
};

const AddDialog = ({ handleClose, afterSubmit }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [selectedBehaviour, setSelectedBehaviour] = useState<number | null>(null);

  const { behaviours, loading: loadingBehaviours } = useGetBehaviours();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    if (selectedBehaviour === null) {
      setErrors({ behaviour: ['É necessário selecionar um comportamento.'] });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/disposition_option', {
        name: capitalizeFirstLetter(data.name as string),
        behaviour: selectedBehaviour,
      });

      if (response?.status === 200) {
        toast.success('Opção de tabulação criada com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch {
      toast.error('Erro ao criar opção de tabulação. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Nova opção de tabulação</Dialog.Title>
            </Dialog.Header>

            {(loadingBehaviours || behaviours.length < 1) ? <Loading /> : (
              <form onSubmit={onSubmit}>
                <Dialog.Body>
                  <Field.Root invalid={!!errors?.name}>
                    <Field.Label>Nome</Field.Label>
                    <InputGroup width="full" gap={0}>
                      <Input name="name" placeholder="Digite aqui..." minLength={3} variant="subtle" required />
                    </InputGroup>
                    {errors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>
                  <Box mt={4}>
                    <Text fontWeight={500} mb={1}>Comportamento</Text>
                    <NativeSelect.Root size="md" variant="subtle" invalid={!!errors?.behaviour}>
                      <NativeSelect.Field
                        placeholder="Selecione uma opção"
                        onChange={(e) => setSelectedBehaviour(Number(e.currentTarget.value))}
                      >
                        {behaviours.map((behaviour) => (
                          <option key={behaviour.id} value={behaviour.id}>
                            {behaviour.name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {errors?.behaviour?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>
                  {errors?.general && (
                    <Flex direction="column" my={4}>
                      {errors.general.map((error, index) => (
                        <ErrorLabel key={index} text={error} />
                      ))}
                    </Flex>
                  )}
                </Dialog.Body>

                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline" disabled={loading}>
                      Cancelar
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button bg="green.700" color="white" type="submit" loading={loading}>
                    Salvar
                  </Button>
                </Dialog.Footer>
              </form>
            )}

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AddDialog;
